import * as React from "react";
import { Box, Button, Checkbox, List, ListItem, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import { Field } from "@/hooks/api/use-fields";

export interface ColumnSelectorProps {
	fields: Field[];
	selectedColumns: string[];
	onColumnToggle: (columnName: string) => void;
	onResetToDefault?: () => void;
	onSelectAll?: () => void;
}

export function ColumnSelector({
	fields,
	selectedColumns,
	onColumnToggle,
	onResetToDefault,
	onSelectAll,
}: ColumnSelectorProps): React.JSX.Element {
	const handleToggle = (columnId: string) => {
		onColumnToggle(columnId);
	};

	const buildColumnTree = () => {
		return (
			<>
				{fields.length > 0 && (
					<Box>
						{fields.map((field) => (
							<ListItem key={field.id} disablePadding>
								<ListItemButton
									onClick={() => handleToggle(field.name)}
									dense
									sx={{ py: 1, gap: 1, alignItems: "center" }}
								>
									<ListItemIcon>
										<Checkbox
											checked={selectedColumns.includes(field.name)}
											size="small"
											sx={{ width: "20px", height: "20px" }}
										/>
									</ListItemIcon>
									<ListItemText
										primary={field.label || field.name}
										sx={{
											"& .MuiListItemText-primary": { fontSize: "0.875rem" },
										}}
									/>
								</ListItemButton>
							</ListItem>
						))}
					</Box>
				)}
			</>
		);
	};

	return (
		<Box sx={{ minWidth: 340, maxHeight: 400 }}>
			{fields.length > 0 && (onResetToDefault || onSelectAll) && (
				<Box
					sx={{
						p: 1,
						px: 2,
						borderBottom: 1,
						borderColor: "divider",
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
					}}
				>
					<Box
						sx={{
							fontWeight: 600,
							fontSize: "0.75rem",
							color: "text.secondary",
							textTransform: "uppercase",
							letterSpacing: "0.5px",
						}}
					>
						Additional Columns
					</Box>
					<Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
						{onSelectAll && (
							<Button size="small" variant="outlined" onClick={onSelectAll} sx={{ fontSize: "0.75rem", px: 1 }}>
								Select All
							</Button>
						)}
						{onResetToDefault && (
							<Button size="small" variant="outlined" onClick={onResetToDefault} sx={{ fontSize: "0.75rem", px: 1 }}>
								Reset
							</Button>
						)}
					</Box>
				</Box>
			)}

			<Box sx={{ maxHeight: 320, overflow: "auto" }}>
				<List disablePadding>{buildColumnTree()}</List>
			</Box>
		</Box>
	);
}
